import React, { useCallback, useContext, useEffect, useState } from "react";
import Payment from "./NewCart/Payment";
import NewCart from "./NewCart";
import ChooseDelivery from "./NewCart/ChooseDelivery";
import { setDeliveryMethod } from "../Actions/articleActions";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Modal,
  SimpleGrid,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  AppContext,
  SET_IDENTIFIER,
  SET_PAYMENT_METHOD,
  CLEAR_PAYMENT_METHOD,
} from "../context";
import { STATE_CULTURE, STATE_CURRENCY } from "../Actions/cookiesActions";
import { INVOICE, SWEDBANKPAY } from "../Components/Payments/constants";
import toast, { ToastBar, Toaster } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { RiCloseFill } from "react-icons/ri";
import { HiOutlineExclamationCircle } from "react-icons/hi";

const useStyles = createStyles(() => ({
  newCheckout: {
    display: "grid",
    gridTemplateColumns: "0.65fr 0.35fr",
    gridGap: "2rem",
    width: "100%",
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "2rem 1rem",
    "@media screen and (max-width: 1080px)": {
      gridTemplateColumns: "1fr 1fr",
    },
    "@media screen and (max-width: 1050px)": {
      gridTemplateColumns: "1fr",
    },
  },
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  checkoutSteps: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  firstStep: {
    border: "1px solid #EAECEE",
    padding: "1.5rem",
    borderRadius: "8px",
    backgroundColor: "#fff",
    h2: {
      marginTop: 0,
    },
    "@media screen and (max-width: 768px)": {
      padding: "1rem",
    },
  },
  headerWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: "1rem",
    alignItems: "end",
    "@media screen and (max-width: 768px)": {
      flexDirection: "column",
      alignItems: "start",
      gap: "1rem",
    },
  },
  title: { marginBottom: "0.2rem", marginTop: 0 },
  subtitle: { fontSize: "0.875rem" },
  step: {
    display: "grid",
    border: "1px solid #EAECEE",
    padding: "1.5rem",
    borderRadius: "8px",
    height: "fit-content",
    marginBottom: "1rem",
    backgroundColor: "#fff",
    "@media screen and (max-width: 768px)": {
      padding: "1rem",
    },
  },
  stepWrapper: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1.5rem",
    "@media screen and (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  textWrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: ".8rem",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "start",
    gap: ".5rem",
    "@media screen and (max-width: 768px)": {
      flexDirection: "column",
      alignItems: "start",
    },
  },
  input: {
    width: "100%",
    "& label": { fontWeight: 700, marginBottom: "0.5rem" },
  },
  addButton: {
    alignSelf: "end",
    height: "38px",
  },
  accordion: {
    marginTop: 0,
  },
  accordionTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    display: "flex",
  },
  accordionSubTitle: {
    fontWeight: 400,
    fontSize: "0.875rem",
    color: "#323232",
    marginTop: "0.2rem",
  },
  accordionControl: {
    padding: "0 8px 0 16px",
    alignItems: "center",
  },
  accordionChevron: {
    padding: "16px 0",
    width: "24px",
    justifyContent: "center",
  },
  accordionIcon: {
    padding: "16px 0",
  },
  label: {
    margin: "0px 0px 8px -6px",
  },
  labelText: {
    lineHeight: 1.2,
  },
  titleAsteriskWrapper: {
    display: "flex",
    gap: "0.1rem",
  },
  segmentedControl: {
    zIndex: 0,
  },
  grayedOut: {
    opacity: 0.5,
    filter: "grayscale(1)",
  },
  countdownWrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: "1rem",
    alignItems: "end",
  },
  buttonTimerWrapper: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "end",
    gap: "1rem",
    "@media screen and (max-width: 768px)": {
      width: "100%",
      justifyContent: "space-between",
    },
    "@media screen and (max-width: 300px)": {
      flexDirection: "column",
      alignItems: "start",
      justifyContent: "space-between",
    },
  },
  toastCloseButtonWrapper: {
    display: "flex",
    alignItems: "start",
    height: "100%",
  },
  toastDismissButton: {
    fontSize: "1.5rem",
    color: "#fff",
  },
}));

const Checkout = ({
  sale,
  language,
  posId,
  isOnePageCheckoutActive,
  setIsOnePageCheckoutActive,
  deliveryMethods,
  authenticated,
  newId,
  responsible,
  selected,
  setSelected,
  ticketOwners,
  selectedDeliveryMethod,
  providesResponsible,
  paymentMethods,
  invalid,
  validateForm,
  hasFee,
  shippingFee,
  responsibleData,
  deliveryMethod,
  enableOptIn,
  whoami,
}) => {
  const [selectedPaymentId, setSelectedPaymentId] = useState("");
  const [invoiceReference, setInvoiceReference] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const context = useContext(AppContext);
  const { config, culture, currency } = useSelector((state) => ({
    config: state.config,
    culture: state.cookies[STATE_CULTURE],
    currency: state.cookies[STATE_CURRENCY],
  }));
  const ctxDispatch = context.dispatch;
  const handleSelect = (value) => {
    const selectedDelMethod = deliveryMethods.find(
      (method) => method.id === value
    );
    dispatch(setDeliveryMethod(sale.id, selectedDelMethod.id));
  };
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveMethods = useCallback(
    () => Object.values(paymentMethods).filter((m) => m.isActive),
    [paymentMethods]
  );
  const dispatchPaymentMethod = (id) => {
    setSelectedPaymentId(id);
    ctxDispatch({ type: SET_PAYMENT_METHOD, payload: id });
  };
  const items = sale.items && sale.items.filter((item) => !item.article.isFee);

  const restartOnePageModal = () => {
    ctxDispatch({ type: SET_PAYMENT_METHOD, payload: SWEDBANKPAY });
    setIsOnePageCheckoutActive(true);
  };

  useEffect(() => {
    if (selectedPaymentId === INVOICE) {
      setModalOpen(true);
    } else {
      setModalOpen(false);
    }
  }, [selectedPaymentId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const errors = searchParams.get("errors");
    const swedbankPayRedirect = searchParams.get("swedbankpay-checkout-url");

    if (swedbankPayRedirect) {
      restartOnePageModal();
    }

    if (language && searchParams.get("errors")) {
      toast.error(language?.ServerMessage[errors], {
        icon: (
          <div className={classes.toastDismissButton}>
            <HiOutlineExclamationCircle />
          </div>
        ),
        duration: 15000,
        iconTheme: {
          primary: "#fff",
          secondary: "#ef4444",
        },
        style: {
          borderRadius: "10px",
          background: "#ef4444",
          color: "#fff",
          minHeight: "10rem",
        },
      });

      searchParams.delete("errors");

      const newSearch = searchParams.toString();
      const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

      navigate(newPath, { replace: true });
    }
  }, [navigate, location, language]);

  return (
    <div className={classes.newCheckout}>
      <main className={classes.checkoutSteps} id="main-page-content">
        <Toaster position="bottom-center">
          {(t) => (
            <ToastBar toast={t}>
              {({ icon, message }) => (
                <>
                  {icon}
                  {message}
                  {t.type !== "loading" && (
                    <div className={classes.toastCloseButtonWrapper}>
                      <UnstyledButton
                        className={classes.toastDismissButton}
                        onClick={() => toast.dismiss(t.id)}
                        aria-label={language.Close}
                      >
                        <RiCloseFill />
                      </UnstyledButton>
                    </div>
                  )}
                </>
              )}
            </ToastBar>
          )}
        </Toaster>
        <Payment
          posId={posId}
          sale={sale}
          language={language}
          getActiveMethods={getActiveMethods()}
          dispatchPaymentMethod={dispatchPaymentMethod}
          isOnePageCheckoutActive={isOnePageCheckoutActive}
          classes={classes}
          selectedPaymentId={selectedPaymentId}
          setSelectedPaymentId={setSelectedPaymentId}
        />
        <Modal
          centered
          opened={modalOpen}
          onClose={() => {
            if (context.state.identifier.length < 1) {
              setSelectedPaymentId(null);
              ctxDispatch({ type: CLEAR_PAYMENT_METHOD });
            }
            setModalOpen(false);
          }}
          title={language.Invoice}
          sx={{ title: { fontWeight: 500 } }}
          closeButtonProps={{ label: language.Close }}
        >
          <SimpleGrid cols={1}>
            <TextInput
              label={language.Invoice_Reference_Label}
              placeholder={language.Invoice_Reference_Placeholder}
              withAsterisk
              value={invoiceReference}
              onChange={(e) => setInvoiceReference(e.currentTarget.value)}
            />
            <Button
              className={"primaryButton"}
              size="md"
              onClick={() => {
                setModalOpen(false);
                ctxDispatch({
                  type: SET_IDENTIFIER,
                  payload: invoiceReference,
                });
              }}
              disabled={invoiceReference.length < 1}
              aria-label={language.Invoice_Reference_Add}
            >
              {language.Save}
            </Button>
          </SimpleGrid>
        </Modal>
        <ChooseDelivery
          sale={sale}
          classes={classes}
          language={language}
          deliveryMethods={deliveryMethods}
          handleSelect={handleSelect}
          fee={shippingFee}
          authenticated={authenticated}
          hasFee={hasFee}
          newId={newId}
          responsible={responsible}
          selected={selected}
          setSelected={setSelected}
          ticketOwners={ticketOwners}
          selectedDeliveryMethod={selectedDeliveryMethod}
          providesResponsible={providesResponsible}
          paymentMethods={paymentMethods}
          shippingFee={shippingFee}
          config={config}
          setIsOnePageCheckoutActive={setIsOnePageCheckoutActive}
          culture={culture}
          currency={currency}
          whoami={whoami}
          validateForm={validateForm}
        />
      </main>
      <NewCart
        saleItems={items}
        sale={sale}
        saleId={sale.id}
        invalid={invalid}
        noItems={sale.items.length < 1}
        paymentMethods={paymentMethods}
        providesResponsible={providesResponsible}
        setIsOnePageCheckoutActive={setIsOnePageCheckoutActive}
        responsibleData={responsibleData}
        deliveryMethod={deliveryMethod}
        deliveryMethods={deliveryMethods}
        enableOptIn={enableOptIn}
      />
    </div>
  );
};

export default Checkout;
